/**
 * The Selling Partner authorizations a set of Amazon Ads profiles implies.
 *
 * Amazon grants Selling Partner access per seller account for a whole region,
 * so the work is one authorization per `(merchantId, region)` pair — a count the
 * Ads profiles already answer, before any Shop exists. Deriving it here keeps
 * onboarding from hardcoding "step 2" as a single row.
 */
import type { AmazonProfile, SpApiConnection, SpApiRegion } from "@adpilot/shared";
import { spApiRegionForMarketplace } from "@adpilot/shared";

export interface SellingPartnerGroup {
  /** Stable row key, also what the connect request names. */
  merchantId: string;
  region: SpApiRegion;
  /** Account name from the Ads profile, shown when several groups compete for the same row. */
  accountName: string;
  /** Marketplace country codes this authorization covers. */
  countryCodes: string[];
}

/**
 * Only seller accounts are grouped: a vendor profile carries a non-Selling-Partner
 * id in `merchantId`, and a profile whose type Amazon has not confirmed cannot be
 * offered an authorization it may not be able to complete.
 */
export function sellingPartnerGroups(profiles: readonly AmazonProfile[]): SellingPartnerGroup[] {
  const groups = new Map<string, SellingPartnerGroup>();

  for (const profile of profiles) {
    if (profile.accountType !== "seller") continue;
    const merchantId = profile.merchantId;
    if (!merchantId) continue;
    const region = spApiRegionForMarketplace(profile.marketplaceId);
    if (!region) continue;

    const key = `${merchantId}:${region}`;
    const existing = groups.get(key);
    if (existing) {
      if (!existing.countryCodes.includes(profile.countryCode)) {
        existing.countryCodes.push(profile.countryCode);
      }
      continue;
    }
    groups.set(key, {
      merchantId,
      region,
      accountName: profile.name,
      countryCodes: [profile.countryCode],
    });
  }

  return [...groups.values()];
}

export function sellingPartnerGroupKey(group: Pick<SellingPartnerGroup, "merchantId" | "region">) {
  return `${group.merchantId}:${group.region}`;
}

/** A group is authorized once a live connection exists for the same seller and region. */
export function isSellingPartnerGroupConnected(
  group: Pick<SellingPartnerGroup, "merchantId" | "region">,
  connections: readonly SpApiConnection[],
): boolean {
  return connections.some(
    (connection) =>
      connection.sellingPartnerId === group.merchantId &&
      connection.region === group.region &&
      connection.status === "connected",
  );
}
